import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { useSession } from "../lib/auth.ts";
import { fmtDurationMs, api, testResultLabel, testResultPillClass, testTypeLabel } from "../lib/api.ts";
import { ChartIcon, EmptyState } from "../components/EmptyState.tsx";
import { useSortableTable, SortHeader } from "../components/SortableTable.tsx";

export function TestsPage() {
  const { data: session } = useSession();
  const orgId = session?.session.activeOrganizationId;
  const nav = useNavigate();
  const tests = useQuery({
    queryKey: ["tests", orgId, 200],
    queryFn: () => api.listTests(orgId!, 200),
    enabled: !!orgId,
  });
  const sortable = useSortableTable(tests.data?.tests ?? [], {
    startedAt:  (t) => t.startedAt,
    name:       (t) => (t.name ?? "").toLowerCase(),
    // Sort by the rendered label — device name when known, fall back to
    // the cloud id — so name-and-id rows interleave the way the user
    // reads them in the column.
    deviceId:   (t) => (t.deviceName ?? t.deviceId).toLowerCase(),
    testType:   (t) => testTypeLabel(t.testType),
    testResult: (t) => testResultLabel(t.testResult),
    durationMs: (t) => t.durationMs,
  }, "startedAt", "desc");

  return (
    <>
      <h1>Tests</h1>
      {tests.isLoading && <p className="muted">Loading…</p>}
      {tests.error && <p className="err">Couldn't load tests — {(tests.error as Error).message}</p>}
      {tests.data && tests.data.tests.length === 0 && (
        <EmptyState
          icon={<ChartIcon />}
          title="No tests yet"
          message="Tests run on paired devices show up here once they finish syncing."
          action={<Link to="/devices">Go to devices</Link>}
        />
      )}
      {tests.data && tests.data.tests.length > 0 && (
        <table>
          <thead>
            <tr>
              <SortHeader label="When"     columnKey="startedAt"  sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Name"     columnKey="name"       sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Device"   columnKey="deviceId"   sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Type"     columnKey="testType"   sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Result"   columnKey="testResult" sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Duration" columnKey="durationMs" sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
            </tr>
          </thead>
          <tbody>
            {sortable.sorted.map((t) => (
              <tr key={t.id} onClick={() => nav(`/tests/${t.id}`)}>
                <td>{new Date(t.startedAt).toLocaleString()}</td>
                <td>{t.name ?? <span className="muted">—</span>}</td>
                <td title={t.deviceId}>
                  {t.deviceName
                    ? t.deviceName
                    : <code className="code-sm">{t.deviceId.slice(0, 12)}…</code>}
                </td>
                <td>{testTypeLabel(t.testType)}</td>
                <td>
                  <span className={testResultPillClass(t.testResult)}>
                    {testResultLabel(t.testResult)}
                  </span>
                </td>
                <td>{fmtDurationMs(t.durationMs)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
